import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, useDropdownMenu } from 'src/components/DropdownMenu';
import { Appointment } from 'src/models/appointment/_types';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import { VisitStatusUpdateOptions } from 'src/shared/AppointmentEventCard/types';
import toast from 'src/utils/toast';

import { CheckInBlockerDialog } from '../CheckinBlocker/CheckInBlockerDialog';
import { useCheckInBlocker } from '../CheckinBlocker/useCheckInBlocker';

import { VisitStatusButton } from './VisitStatusButton';
import { VisitStatusDropdownItems } from './VisitStatusDropdownItems';

interface Props {
  visit: Visit;
  appointment: Appointment;
  isTrainerView?: boolean;
  compact?: boolean;
  afterUpdateStatus?: (visit: Visit) => void;
  onUpdateVisitStatus?: (
    visit: Visit,
    status: VisitStatus,
    options?: VisitStatusUpdateOptions
  ) => void | Promise<void>;
}

export const VisitStatusDropdown: React.FC<Props> = ({
  visit,
  appointment,
  isTrainerView = true,
  compact = true,
  afterUpdateStatus,
  onUpdateVisitStatus,
}) => {
  const dropdownMenu = useDropdownMenu();

  const updateVisitMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit(visit);
    },
    onSuccess: (updatedVisit) => {
      afterUpdateStatus?.(updatedVisit);
      toast.success('Visit(s) successfully updated!');
    },
    onError: () => {
      toast.error('Visit could not be updated');
    },
  });

  const { t: tCheckinBlocker } = useTranslation('translation', {
    keyPrefix: 'checkinBlocker',
  });

  const updateVisitStatus = async (
    visit: Visit,
    status: VisitStatus,
    additionalParams: VisitStatusUpdateOptions = {},
    overrideCheckInBlocker= false
  ) => {
    if (onUpdateVisitStatus) {
      void onUpdateVisitStatus(visit, status, additionalParams);
      return;
    }
    if (status === VisitStatus.Completed && !overrideCheckInBlocker) {
      const blockers = await checkBlockers({ userIds: [visit.userId ?? ''] });
      if (blockers.length > 0) {
        return;
      }
    }
    updateVisitMutation.mutate({ ...visit, status, ...additionalParams });
  };

  const [checkInBlockerDialogProps, checkBlockers] = useCheckInBlocker({
    title: tCheckinBlocker('actionRequired'),
    onOverride: isTrainerView
      ? async () => {
          await updateVisitStatus(visit, VisitStatus.Completed, {}, true);
          checkInBlockerDialogProps.onClose?.();
        }
      : undefined,
  });
  return (
    <>
      <DropdownMenu
        open={dropdownMenu.isOpen}
        setOpen={dropdownMenu.setIsOpen}
        closeOnContentClick={false}
        trigger={
          <VisitStatusButton
            visit={visit}
            setOpen={dropdownMenu.setIsOpen}
            open={dropdownMenu.isOpen}
            compact={compact}
          />
        }
      >
        <VisitStatusDropdownItems
          visit={visit}
          appointment={appointment}
          isTrainerView={isTrainerView}
          setIsOpen={dropdownMenu.setIsOpen}
          updateVisitStatus={updateVisitStatus}
        />
      </DropdownMenu>
      <CheckInBlockerDialog {...checkInBlockerDialogProps} />
    </>
  );
};
